import React, { useState } from "react"
import { Formik } from "formik"
import * as Yup from "yup"
import { NavLink, Redirect, useLocation } from "react-router-dom"
import useAuth from "../../context/userContext"
import { ErrorNotification } from "../../utils"
import { RiEyeCloseLine, RiEyeLine } from "react-icons/ri"
import { SlantedWrapper, SVGLogo } from "../../components"
import { FaRegEyeSlash } from "react-icons/fa"

const Login = () => {
  const [loading, setLoading] = useState(false)
  const { redirectToReferrer, signin } = useAuth()
  const { state } = useLocation()
  const [showPassword, setShowPassword] = useState(false)

  const login = async (values) => {
    setLoading(true)
    try {
      await signin(values)
      setLoading(false)
    } catch (error) {

      setLoading(false)
      ErrorNotification(error.message || "Something went wrong. Please check and try again.")
    }
  }

  if (redirectToReferrer) {
    return <Redirect to={state?.from || "/admin/dashboard"} />
  }

  return (
    <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-2 bg-white dark:bg-dark">
      {/* Left: Login Form */}
      <div className="flex flex-col justify-center items-center px-4 md:px-0  min-h-screen">
        <div className="w-full max-w-sm mx-auto flex flex-col justify-center py-8 max-h-[calc(100vh-40px)] overflow-y-auto">
          {/* Logo and Brand */}
          <div className="flex items-center justify-center mb-6">
            <span className="inline-flex items-center gap-3">
              <SVGLogo />
              <span className="text-2xl font-bold" style={{ color: '#FDBA2F' }}>FarmerKonnect</span>
            </span>
          </div>
          {/* Heading */}
          <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-1 text-center">Welcome back</h2>
          <p className="text-gray-500 text-center mb-7">Please enter your details to sign in.</p>
          <Formik
            initialValues={{ email: "", password: "" }}
            onSubmit={login}
            validationSchema={Yup.object().shape({
              email: Yup.string().email("Email is invalid").required("Email is required"),
              password: Yup.string().min(4, "Password must be at least 4 characters").required("Password is required"),
            })}
          >
            {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-1 dark:text-white">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your email"
                    className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  />
                  {touched.email && errors.email && (
                    <div className="text-red-500 text-xs mt-1">
                      Please enter a valid email
                    </div>
                  )}
                </div>
                <div>
                  <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-1 dark:text-white">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your password"
                      className="w-full font-normal bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-3.5 text-gray-400 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaRegEyeSlash /> : <RiEyeLine />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <div className="text-red-500 text-xs mt-1">{errors.password}</div>
                  )}
                </div>
                <div className="flex justify-end -mt-2 mb-2">
                  <NavLink to="/forgot-password" className="text-yellowcolor text-sm hover:underline">Forgot Password?</NavLink>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || loading}
                  className="w-full bg-yellowcolor hover:bg-yellowcolor/90 text-black font-normal py-3 rounded-md text-base transition-colors duration-200 mb-2 shadow-none"
                >
                  {loading ? "Loading..." : "Sign In"}
                </button>
                <div className="text-center font-normal text-gray-500 mt-2">
                  Don't have an account?{' '}
                  <NavLink to="/signup" className="text-yellowcolor hover:underline font-medium">Create an account</NavLink>
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
      {/* Right: Image Grid */}
      <SlantedWrapper />
    </div>
  )
}

export default Login